import { PDFDocument } from 'pdf-lib';

/**
 * Gets the total number of pages in a PDF file
 * @param {File} file - The PDF file
 * @returns {Promise<number>} - The number of pages
 */
export async function getPdfPageCount(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  return pdfDoc.getPageCount();
}

/**
 * Extracts a single page from a PDF and returns it as a Blob
 * @param {File} file - The original PDF file
 * @param {number} pageIndex - The 0-based index of the page to extract
 * @returns {Promise<Blob>} - The single-page PDF as a Blob
 */
export async function extractPdfPage(file, pageIndex) {
  const arrayBuffer = await file.arrayBuffer();
  const srcDoc = await PDFDocument.load(arrayBuffer);

  const newDoc = await PDFDocument.create();
  const [copiedPage] = await newDoc.copyPages(srcDoc, [pageIndex]);
  newDoc.addPage(copiedPage);

  const pdfBytes = await newDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
}

/**
 * Splits a PDF file into multiple single-page PDFs based on selected page numbers
 * @param {File} file - The original PDF file
 * @param {number[]} pageNumbers - Array of 1-based page numbers to extract
 * @returns {Promise<{pageNumber: number, blob: Blob, file: File}[]>} - Array of objects containing page info and file
 */
export async function splitPdfPages(file, pageNumbers) {
  const arrayBuffer = await file.arrayBuffer();
  const srcDoc = await PDFDocument.load(arrayBuffer);

  const results = [];

  for (const pageNumber of pageNumbers) {
    const pageIndex = pageNumber - 1;

    const newDoc = await PDFDocument.create();
    const [copiedPage] = await newDoc.copyPages(srcDoc, [pageIndex]);
    newDoc.addPage(copiedPage);

    const pdfBytes = await newDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });

    const baseName = file.name.replace(/\.pdf$/i, '');
    const newFile = new File([blob], `${baseName}_page_${pageNumber}.pdf`, {
      type: 'application/pdf',
    });

    results.push({
      pageNumber,
      blob,
      file: newFile,
    });
  }

  return results;
}
